import { useEffect, useRef } from 'react';
import { clearCache } from '../cacheService';

interface UseAutoRefreshOptions {
  intervalMs?: number;
  onRefresh?: () => void;
}

export const useAutoRefresh = ({ 
  intervalMs = 30000, // 1 minute default
  onRefresh 
}: UseAutoRefreshOptions = {}) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isActiveRef = useRef(true);

  const startAutoRefresh = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      if (isActiveRef.current) {
        // Clear all cache to force fresh data fetch
        clearCache();
        
        // Trigger refresh callback if provided
        if (onRefresh) {
          onRefresh();
        }
        
        console.log('Auto-refresh triggered: Cache cleared, data will be refetched');
      }
    }, intervalMs);
  };

  const stopAutoRefresh = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    // Start auto-refresh when component mounts
    isActiveRef.current = true;
    startAutoRefresh();

    // Handle visibility change to pause/resume when tab is not active
    const handleVisibilityChange = () => {
      isActiveRef.current = !document.hidden;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on unmount
    return () => {
      isActiveRef.current = false;
      stopAutoRefresh();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [intervalMs, onRefresh]);

  return {
    startAutoRefresh,
    stopAutoRefresh
  };
}; 